import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

const DiscountsAdminPage: React.FC = () => {
  const [discounts, setDiscounts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    type_: 'percentage',
    value: '',
    minimum_order: '',
    usage_limit: '',
    is_active: true,
    expires_at: '',
  });

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      setIsLoading(true);
      const res = await adminAPI.getAllDiscounts();
      if (res.success && res.data) setDiscounts(res.data);
    } catch {
      toast.error('Failed to fetch discounts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminAPI.createDiscount({
        ...formData,
        value: parseFloat(formData.value),
        minimum_order: formData.minimum_order ? parseFloat(formData.minimum_order) : 0,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : undefined,
        expires_at: formData.expires_at || undefined,
      });
      toast.success('Discount created');
      setShowForm(false);
      setFormData({
        code: '', type_: 'percentage', value: '',
        minimum_order: '', usage_limit: '', is_active: true, expires_at: '',
      });
      fetchDiscounts();
    } catch {
      toast.error('Failed to create discount');
    }
  };

  const handleToggle = async (id: string, is_active: boolean) => {
    try {
      await adminAPI.updateDiscount(id, { is_active: !is_active });
      toast.success('Discount updated');
      fetchDiscounts();
    } catch {
      toast.error('Failed to update discount');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this discount?')) return;
    try {
      await adminAPI.deleteDiscount(id);
      toast.success('Discount deleted');
      fetchDiscounts();
    } catch {
      toast.error('Failed to delete discount');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-widest uppercase text-nude mb-1">Management</p>
          <h1 className="font-serif text-3xl text-primary">Discounts</h1>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={14} />
          <span>Add Discount</span>
        </button>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-light p-6"
        >
          <h2 className="font-serif text-xl text-primary mb-6">New Discount Code</h2>
          <form onSubmit={handleCreate} className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">Code</label>
              <input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="input-luxury"
                required
                placeholder="SUMMER20"
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">Type</label>
              <select
                value={formData.type_}
                onChange={(e) => setFormData({ ...formData, type_: e.target.value })}
                className="input-luxury"
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Value {formData.type_ === 'percentage' ? '(%)' : '($)'}
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                className="input-luxury"
                required
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Minimum Order ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.minimum_order}
                onChange={(e) => setFormData({ ...formData, minimum_order: e.target.value })}
                className="input-luxury"
                placeholder="0"
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Usage Limit
              </label>
              <input
                type="number"
                value={formData.usage_limit}
                onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                className="input-luxury"
                placeholder="Unlimited"
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Expires At
              </label>
              <input
                type="datetime-local"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                className="input-luxury"
              />
            </div>
            <div className="col-span-2 flex space-x-3">
              <button type="submit" className="btn-primary">Create Discount</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      <div className="bg-white border border-light overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-light">
              {['Code', 'Type', 'Value', 'Min Order', 'Usage', 'Expires', 'Status', 'Actions'].map((h) => (
                <th key={h} className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center">
                  <div className="skeleton h-6 w-48 mx-auto rounded" />
                </td>
              </tr>
            ) : discounts.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-xs text-nude tracking-widest uppercase">
                  No discount codes
                </td>
              </tr>
            ) : (
              discounts.map((d) => (
                <tr key={d.id} className="border-b border-light hover:bg-light/50 transition-colors">
                  <td className="px-6 py-4 text-xs font-mono font-medium">{d.code}</td>
                  <td className="px-6 py-4 text-xs text-nude tracking-widest uppercase">{d.type}</td>
                  <td className="px-6 py-4 text-xs">
                    {d.type === 'percentage' ? `${d.value}%` : `$${Number(d.value).toFixed(2)}`}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    ${Number(d.minimum_order).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-xs">
                    {d.used_count}{d.usage_limit ? `/${d.usage_limit}` : ''}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    {d.expires_at ? new Date(d.expires_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggle(d.id, d.is_active)}
                      className={`text-xs tracking-widest uppercase px-2 py-1 rounded transition-colors ${
                        d.is_active
                          ? 'bg-green-50 text-green-600 hover:bg-red-50 hover:text-red-500'
                          : 'bg-red-50 text-red-500 hover:bg-green-50 hover:text-green-600'
                      }`}
                    >
                      {d.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleDelete(d.id)}
                      className="text-nude hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DiscountsAdminPage;